import React, { useEffect, useMemo, useState } from "react";
import Select, { components, GroupBase, MultiValueProps, ValueContainerProps } from "react-select";
import { useTranslation } from "react-i18next";
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS as DndCSS } from "@dnd-kit/utilities";

interface TimeZone {
    value: string;
    label: string;
    region: string;
    city: string;
}

interface IProps {
    selectedTimezones: string[];
    onChange: (timezones: string[]) => void;
    placeholder?: string;
    isInvalid?: boolean;
}

const formatTimezoneName = (timezone: string): { label: string; region: string; city: string } => {
    const parts = timezone.split("/");
    if (parts.length === 1) {
        return {
            label: timezone,
            region: "Other",
            city: timezone,
        };
    }
    const region = parts[0].replace(/_/g, " ");
    const city = parts[parts.length - 1].replace(/_/g, " ");
    const label = parts.length > 2 ? `${city} (${parts.slice(1, -1).join(", ")}) - ${region}` : `${city} - ${region}`;
    return { label, region, city };
};

const isIntlSupported = typeof Intl !== "undefined" && typeof (Intl as any).supportedValuesOf === "function";

export default function TimeZoneMultiSelect(props: IProps) {
    const { t } = useTranslation();
    const [selectedOptions, setSelectedOptions] = useState<TimeZone[]>([]);

    // Build all time zone options once (memoized)
    const timezoneOptions: TimeZone[] = useMemo(() => {
        if (!isIntlSupported) {
            console.warn("Intl.supportedValuesOf is not supported in this browser");
            return [];
        }
        try {
            const timezones: string[] = (Intl as any).supportedValuesOf("timeZone");
            const structured = timezones.map((tz) => ({
                ...formatTimezoneName(tz),
                value: tz,
            }));
            // Sort by region then city for UX
            structured.sort((a, b) =>
                a.region === b.region ? a.city.localeCompare(b.city) : a.region.localeCompare(b.region),
            );
            return structured;
        } catch (err) {
            console.error("Error accessing timezone data:", err);
            return [];
        }
    }, []);

    // Map props.selectedTimezones -> local selectedOptions (objects)
    useEffect(() => {
        if (!props.selectedTimezones?.length || !timezoneOptions.length) {
            setSelectedOptions([]);
            return;
        }
        const map = new Map(timezoneOptions.map((opt) => [opt.value, opt]));
        const selected = props.selectedTimezones
            .map((tz) => map.get(tz))
            .filter((opt): opt is TimeZone => opt !== undefined);
        setSelectedOptions(selected);
    }, [props.selectedTimezones, timezoneOptions]);

    // onChange handler; enforces max 6; syncs parent onChange
    const handleChange = (newValue: any) => {
        const options = (newValue || []) as TimeZone[];
        if (options.length > 6) return; // enforce max
        setSelectedOptions(options);
        props.onChange(options.map((o) => o.value));
    };

    // Group by region for react-select grouped options
    const groupedOptions: GroupBase<TimeZone>[] = useMemo(() => {
        const groups: Record<string, TimeZone[]> = {};
        timezoneOptions.forEach((opt) => {
            (groups[opt.region] ||= []).push(opt);
        });
        return Object.entries(groups).map(([region, options]) => ({ label: region, options }));
    }, [timezoneOptions]);

    // Styles (preserve original look & validations)
    const customStyles = {
        control: (base: any, state: any) => ({
            ...base,
            minHeight: "36px",
            background: "#FFFFFF",
            boxShadow: "inset 0px 2px 4px rgba(31, 39, 71, 0.1)",
            borderRadius: "8px",
            border: props.isInvalid ? "1px solid #F44336" : state.isFocused ? "1px solid #6C89E8" : "1px solid #C6C8CE",
            "&:hover": {
                border: props.isInvalid ? "1px solid #F44336" : "1px solid #4B5168",
            },
        }),
        multiValue: (base: any) => ({
            ...base,
            backgroundColor: "#E8EBF0",
            borderRadius: "4px",
        }),
        multiValueLabel: (base: any) => ({
            ...base,
            color: "#1F2747",
            padding: "2px 6px",
        }),
        multiValueRemove: (base: any) => ({
            ...base,
            color: "#6C7189",
            ":hover": {
                backgroundColor: "#C6C8CE",
                color: "#1F2747",
            },
        }),
        menu: (base: any) => ({
            ...base,
            zIndex: 9999,
        }),
        menuList: (base: any) => ({
            ...base,
            maxHeight: "300px",
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isSelected ? "#6C89E8" : state.isFocused ? "#F3F4F5" : "white",
            color: state.isSelected ? "white" : "#1F2747",
            ":active": {
                backgroundColor: "#6C89E8",
                color: "white",
            },
        }),
    };

    // -------------------- DND-KIT INTEGRATION --------------------
    // We make chips (MultiValue) sortable. We wrap the ValueContainer with a DndContext and
    // render each MultiValue inside a useSortable wrapper. On drop, we reorder and call onChange.

    // Sensors: drag starts after small pointer movement to avoid accidental drags on click.
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        }),
    );

    // Sortable chip wrapper – attaches dnd-kit attributes/listeners to the chip
    const SortableMultiValue = (mvProps: MultiValueProps<TimeZone>) => {
        const id = mvProps.data.value; // stable id per chip
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

        const style: React.CSSProperties = {
            // Keep inline-flex so react-select’s layout remains intact
            display: "inline-flex",
            transform: DndCSS.Transform.toString(transform),
            transition: transition ?? undefined,
            cursor: isDragging ? "grabbing" : "grab",
            opacity: isDragging ? 0.6 : 1,
        };

        // We render the library's default MultiValue inside our draggable wrapper.
        return (
            <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
                <components.MultiValue {...mvProps} />
            </div>
        );
    };

    // ValueContainer wrapper – provides DndContext + SortableContext around the chips
    const SortableValueContainer = (vcProps: ValueContainerProps<TimeZone, true>) => {
        const valueArray = (vcProps.selectProps.value || []) as TimeZone[];
        const ids = valueArray.map((o) => o.value);

        const onDragEnd = (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            const oldIndex = ids.indexOf(String(active.id));
            const newIndex = ids.indexOf(String(over.id));
            if (oldIndex === -1 || newIndex === -1) return;

            const newOrder = arrayMove(valueArray, oldIndex, newIndex);
            // IMPORTANT: trigger react-select onChange pathway (which calls our handleChange)
            vcProps.selectProps.onChange(newOrder as any, { action: "select-option" } as any);
        };

        return (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={ids} strategy={rectSortingStrategy}>
                    <components.ValueContainer {...vcProps} />
                </SortableContext>
            </DndContext>
        );
    };

    // -------------------------------------------------------------

    if (timezoneOptions.length === 0) {
        return (
            <div
                className="timezone-not-supported"
                style={{
                    padding: "10px",
                    backgroundColor: "#FFF3CD",
                    border: "1px solid #FFEAA7",
                    borderRadius: "8px",
                    color: "#856404",
                }}
            >
                {t(
                    "Integrations.Widgets.TimezonesNotSupported",
                    "Timezone selection is not supported in this browser. Please use a modern browser.",
                )}
            </div>
        );
    }

    return (
        <div className="timezone-select-wrapper">
            <Select<TimeZone, true, GroupBase<TimeZone>>
                isMulti
                value={selectedOptions}
                onChange={handleChange}
                options={groupedOptions}
                placeholder={props.placeholder || t("Integrations.Widgets.SelectTimeZones") + " (max 6)"}
                className="timezone-multi-select"
                classNamePrefix="timezone-select"
                styles={customStyles}
                // KEY: enable sortable chips + keep default remove behavior
                components={{
                    IndicatorSeparator: () => null,
                    ValueContainer: SortableValueContainer,
                    MultiValue: SortableMultiValue,
                    // We deliberately do NOT override MultiValueRemove here; default handles the “×” correctly.
                }}
                menuPosition="fixed"
                closeMenuOnSelect={false}
                isSearchable
                isClearable={false}
                backspaceRemovesValue
                noOptionsMessage={() => t("Integrations.Widgets.NoTimezonesFound", "No timezones found")}
                isOptionDisabled={(option) =>
                    selectedOptions.length >= 6 && !selectedOptions.some((o) => o.value === option.value)
                }
            />
            <div
                style={{
                    marginTop: "4px",
                    fontSize: "12px",
                    color: selectedOptions.length >= 6 ? "#F44336" : "#6C7189",
                    display: "flex",
                    justifyContent: "space-between",
                }}
            >
                <span>
                    {selectedOptions.length} / 6 {t("Integrations.Widgets.TimezonesSelected", "timezones selected")}
                </span>
                {selectedOptions.length >= 6 && <span>{t("Integrations.Widgets.MaxReached", "Maximum reached")}</span>}
            </div>
        </div>
    );
}
